import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import { login } from 'actions/app.actions';
import useQuery from 'core/utils/use-query.hook';
import { LoginTypes } from 'interfaces';
import { isAuthenticatedSelector, loginLoadingSelector } from 'reducers/app.reducer';
import redirectAfterAuth from 'utils/redirect-after-auth';

interface ILoginFormType extends LoginTypes {
  rememberMe: boolean;
}

type useLoginOutputType = {
  handleLogin: (value: ILoginFormType) => void;
  initialValues: ILoginFormType;
  loading: boolean;
  loginSchema: Yup.ObjectSchema;
  t: (value: string) => string;
};

const initialValues: ILoginFormType = {
  username: '',
  password: '',
  rememberMe: false,
};

/**
 * Redirige l'utilisateur déjà authentifié qui atterrit sur la page de login : retour arrière
 * du navigateur, lien direct, ou session encore valide.
 */
export const useRedirectWhenAuthenticated = (): void => {
  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const { returnPath } = useQuery();

  useEffect(() => {
    if (isAuthenticated) {
      redirectAfterAuth(returnPath);
    }
  }, [isAuthenticated, returnPath]);
};

const useLoginFormEvents = (): useLoginOutputType => {
  const { t } = useTranslation();
  const loading = useSelector(loginLoadingSelector);
  const { returnPath } = useQuery();
  const dispatch = useDispatch();

  const loginSchema = Yup.object().shape({
    username: Yup.string().required(t('core.required')),
    password: Yup.string().required(t('core.required')),
  });

  /**
   * Remove rememberMe from values before handle login
   * @param values
   */
  const handleLogin = (values: ILoginFormType): void => {
    const { rememberMe, ...rest } = values;
    dispatch(
      login.request(rest, () => {
        // Normal login flow - MFA redirect is handled by the saga
        redirectAfterAuth(returnPath);
      }),
    );
  };

  return {
    handleLogin,
    initialValues,
    loading,
    loginSchema,
    t,
  };
};

export default useLoginFormEvents;
